import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart3,
  TrendingUp,
  Users,
  Calendar,
  Stethoscope,
  ClipboardList,
  Building2,
  MapPin,
  Activity,
  Download,
  RefreshCw,
  PieChart,
  FileSpreadsheet
} from 'lucide-react';
import CustomReportsTab from '../components/reports/CustomReportsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', trend, vsPreviousPeriodText }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${
              trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'
            }`}>
              <TrendingUp className={`h-3 w-3 ${trend < 0 ? 'rotate-180' : ''}`} />
              {Math.abs(trend)}% {vsPreviousPeriodText}
            </div>
          )}
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          color === 'primary' ? 'bg-[#FDE8EA]' :
          color === 'blue' ? 'bg-blue-100' :
          color === 'green' ? 'bg-green-100' :
          color === 'yellow' ? 'bg-yellow-100' : 'bg-gray-100'
        }`}>
          <Icon className={`h-5 w-5 ${
            color === 'primary' ? 'text-[#E63946]' :
            color === 'blue' ? 'text-blue-600' :
            color === 'green' ? 'text-green-600' :
            color === 'yellow' ? 'text-yellow-600' : 'text-gray-600'
          }`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SimpleBarChart = ({ data, labelKey, valueKey, maxValue, color = '#E63946' }) => {
  if (!data || data.length === 0) return null;
  
  const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0));
  
  return (
    <div className="space-y-3">
      {data.map((item, idx) => (
        <div key={item.id || item._id || item[labelKey] || `item-${idx}`} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 truncate max-w-[200px]">
              {item[labelKey] || item.label || item._id || 'N/A'}
            </span>
            <span className="font-medium">{item[valueKey] || item.count || 0}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${max > 0 ? ((item[valueKey] || item.count || 0) / max) * 100 : 0}%`,
                backgroundColor: color
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const SummaryTab = ({ startDate, endDate, t }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, [startDate, endDate]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/summary`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  if (!summary) return null;

  const getStatusLabel = (status) => t(`status.${status}`) || status;
  const getServiceTypeLabel = (type) => t(`serviceTypes.${type}`) || type;

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('summary.totalOrders')}
          value={summary.orders?.total || 0}
          subtitle={`${summary.orders?.completion_rate || 0}% ${t('summary.completedPercent')}`}
          icon={ClipboardList}
          color="primary"
        />
        <StatCard
          title={t('summary.ordersCompleted')}
          value={summary.orders?.completed || 0}
          icon={Activity}
          color="green"
        />
        <StatCard
          title={t('summary.newPatients')}
          value={summary.patients?.new_in_period || 0}
          subtitle={`${summary.patients?.active_total || 0} ${t('summary.activeTotal')}`}
          icon={Users}
          color="blue"
        />
        <StatCard
          title={t('summary.clinicalNotes')}
          value={summary.clinical_notes?.created || 0}
          subtitle={`${summary.clinical_notes?.validated || 0} ${t('summary.validated')}`}
          icon={Stethoscope}
          color="yellow"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders by Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('summary.ordersByStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.orders?.by_status && Object.keys(summary.orders.by_status).length > 0 ? (
              <SimpleBarChart
                data={Object.entries(summary.orders.by_status).map(([status, count]) => ({
                  label: getStatusLabel(status),
                  count
                }))}
                labelKey="label"
                valueKey="count"
              />
            ) : (
              <p className="text-center text-gray-400 py-8">{t('summary.noData')}</p>
            )}
          </CardContent>
        </Card>

        {/* Orders by Service Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('summary.ordersByServiceType')}</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.orders?.by_service_type && Object.keys(summary.orders.by_service_type).length > 0 ? (
              <SimpleBarChart
                data={Object.entries(summary.orders.by_service_type).map(([type, count]) => ({
                  label: getServiceTypeLabel(type),
                  count
                }))}
                labelKey="label"
                valueKey="count"
                color="#3B82F6"
              />
            ) : (
              <p className="text-center text-gray-400 py-8">{t('summary.noData')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const SpecialistsTab = ({ startDate, endDate, t }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/specialists-performance`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching specialists performance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="specialists-report-table">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">{t('specialists.table.specialist')}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">{t('specialists.table.type')}</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">{t('specialists.table.total')}</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">{t('specialists.table.completed')}</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">{t('specialists.table.inProgress')}</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">{t('specialists.table.completionRate')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={item.specialist_id || item.id || `specialist-${idx}`} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Stethoscope className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="font-medium text-sm">{item.specialist_name || t('specialists.noName')}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{item.specialist_type || '-'}</td>
                  <td className="py-3 px-4 text-center font-medium">{item.total_orders}</td>
                  <td className="py-3 px-4 text-center text-green-600">{item.completed}</td>
                  <td className="py-3 px-4 text-center text-blue-600">{item.in_progress}</td>
                  <td className="py-3 px-4 text-center">
                    <Badge className={
                      item.completion_rate >= 80 ? 'bg-green-100 text-green-800' :
                      item.completion_rate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {item.completion_rate}%
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Stethoscope className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{t('specialists.noData')}</p>
        </div>
      )}
    </div>
  );
};

const InsurersTab = ({ startDate, endDate, t }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/insurers`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching insurers report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  return (
    <div>
      {data.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('insurers.ordersByInsurer')}</CardTitle>
            </CardHeader>
            <CardContent>
              <SimpleBarChart
                data={data}
                labelKey="_id"
                valueKey="total_orders"
                color="#10B981"
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('insurers.detail')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.map((item, idx) => (
                  <div key={item._id || `insurer-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-gray-400" />
                      <span className="font-medium">{item._id || t('insurers.unspecified')}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{item.total_orders} {t('insurers.orders')}</p>
                      <p className="text-xs text-green-600">{item.completed} {t('insurers.completed')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{t('insurers.noData')}</p>
        </div>
      )}
    </div>
  );
};

const CitiesTab = ({ startDate, endDate, t }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/cities`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching cities report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  return (
    <div>
      {data.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('cities.geographicDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SimpleBarChart
                data={data}
                labelKey="_id"
                valueKey="total_orders"
                color="#8B5CF6"
              />
              <div className="space-y-3">
                {data.map((item, idx) => (
                  <div key={item._id || `city-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-purple-500" />
                      <span>{item._id || t('cities.unspecified')}</span>
                    </div>
                    <Badge variant="outline">{item.total_orders} {t('cities.orders')}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{t('cities.noData')}</p>
        </div>
      )}
    </div>
  );
};

const ServiceTypesTab = ({ startDate, endDate, t }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/service-types`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching service types report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  return (
    <div>
      {data.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('services.serviceTypes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleBarChart
              data={data}
              labelKey="label"
              valueKey="count"
              color="#F59E0B"
            />
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{t('services.noData')}</p>
        </div>
      )}
    </div>
  );
};

export const ReportsPage = () => {
  const { t } = useTranslation('reports');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const handleExport = () => {
    toast.info(t('exportInDevelopment'));
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-gray-500 mt-1">{t('subtitle')}</p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          {t('export')}
        </Button>
      </div>

      {/* Date Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">{t('period')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('from')}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-auto"
                data-testid="report-start-date"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('to')}</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-auto"
                data-testid="report-end-date"
              />
            </div>
            <div className="flex gap-2 ml-auto">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const date = new Date();
                  date.setDate(date.getDate() - 7);
                  setStartDate(date.toISOString().split('T')[0]);
                  setEndDate(new Date().toISOString().split('T')[0]);
                }}
              >
                {t('days7')}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const date = new Date();
                  date.setDate(date.getDate() - 30);
                  setStartDate(date.toISOString().split('T')[0]);
                  setEndDate(new Date().toISOString().split('T')[0]);
                }}
              >
                {t('days30')}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const date = new Date();
                  date.setDate(date.getDate() - 90);
                  setStartDate(date.toISOString().split('T')[0]);
                  setEndDate(new Date().toISOString().split('T')[0]);
                }}
              >
                {t('days90')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-6">
          <TabsTrigger value="summary" data-testid="summary-tab">
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('tabs.summary')}
          </TabsTrigger>
          <TabsTrigger value="specialists" data-testid="specialists-tab">
            <Stethoscope className="h-4 w-4 mr-2" />
            {t('tabs.specialists')}
          </TabsTrigger>
          <TabsTrigger value="insurers" data-testid="insurers-tab">
            <Building2 className="h-4 w-4 mr-2" />
            {t('tabs.insurers')}
          </TabsTrigger>
          <TabsTrigger value="cities" data-testid="cities-tab">
            <MapPin className="h-4 w-4 mr-2" />
            {t('tabs.cities')}
          </TabsTrigger>
          <TabsTrigger value="services" data-testid="services-tab">
            <PieChart className="h-4 w-4 mr-2" />
            {t('tabs.services')}
          </TabsTrigger>
          <TabsTrigger value="custom" data-testid="custom-tab">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {t('tabs.custom')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <SummaryTab startDate={startDate} endDate={endDate} t={t} />
        </TabsContent>

        <TabsContent value="specialists">
          <Card>
            <CardHeader>
              <CardTitle>{t('specialists.title')}</CardTitle>
              <CardDescription>{t('specialists.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <SpecialistsTab startDate={startDate} endDate={endDate} t={t} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insurers">
          <InsurersTab startDate={startDate} endDate={endDate} t={t} />
        </TabsContent>

        <TabsContent value="cities">
          <CitiesTab startDate={startDate} endDate={endDate} t={t} />
        </TabsContent>

        <TabsContent value="services">
          <ServiceTypesTab startDate={startDate} endDate={endDate} t={t} />
        </TabsContent>

        <TabsContent value="custom">
          <CustomReportsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
